import React, { useState, useEffect, useRef } from "react";
import { MapPin, Star, Heart, Share2, ChevronRight } from "lucide-react";
import axios from "axios";
import Header from "./components/Header";
import PhotoGallery from "./components/PhotoGallery";
import ReservationCard from "./components/ReservationCard";
import { About, MenuPreview, FullMenu } from "./components/RestaurantSections";
import Reviews from "./components/Reviews";
import { LocationSection, AwardsSection, HoursSection } from "./components/SideSections";
import Footer from "./components/Footer";
import MobileCTA from "./components/MobileCTA";
import { restaurant } from "./mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RestaurantPage = () => {
  const [liked, setLiked] = useState(false);
  const [activeTab, setActiveTab] = useState("a-propos");
  const [customReviews, setCustomReviews] = useState([]);
  const galleryRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setCustomReviews(r.data)).catch(() => {});
  }, []);

  const customerPhotos = customReviews.flatMap((r) => r.photos || []);

  const onNewReview = (r) => setCustomReviews((cs) => [r, ...cs]);
  const onOpenPhoto = (src) => galleryRef.current?.openBySrc(src);

  useEffect(() => {
    const sections = ["a-propos", "menu", "avis"];
    const onScroll = () => {
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= 200) {
            setActiveTab(id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 pb-24 lg:pb-0">
      <Header />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-4">
        <nav className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
          <a href="#" className="hover:text-[#1B7B71]">Accueil</a>
          <ChevronRight className="h-3 w-3" />
          <a href="#" className="hover:text-[#1B7B71]">Bougival</a>
          <ChevronRight className="h-3 w-3" />
          <span className="text-gray-700 font-medium">BAAN MAE</span>
        </nav>
      </div>

      {/* Gallery */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 mt-4">
        <PhotoGallery ref={galleryRef} extraPhotos={customerPhotos} />
      </div>

      {/* Restaurant Header */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#D14318] bg-[#FDECE3] px-2 py-1 rounded-md">
                <Star className="h-3 w-3 fill-[#D14318]" /> {restaurant.tagline}
              </span>
              <span className="text-xs text-gray-500">{restaurant.recentReviews} avis ces 30 derniers jours</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">{restaurant.name}</h1>
            <div className="flex items-center gap-3 mt-3 flex-wrap text-sm text-gray-700">
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-[#1B7B71]" /> {restaurant.address}</span>
              <span className="text-gray-300">•</span>
              <span>{restaurant.cuisine}</span>
              <span className="text-gray-300">•</span>
              <span>Prix moyen : {restaurant.avgPrice} €</span>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="bg-[#1B7B71] text-white rounded-md px-2.5 py-1 font-bold text-sm">
                {restaurant.rating.toString().replace(".", ",")}
              </div>
              <a href="#avis" onClick={(e) => { e.preventDefault(); scrollTo("avis"); }} className="text-sm text-gray-700 hover:underline">
                ({restaurant.reviewsCount.toLocaleString("fr-FR")} avis)
              </a>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setLiked(!liked)}
              className={`p-2.5 rounded-full border transition-all ${liked ? "border-[#D14318] bg-[#FDECE3]" : "border-gray-300 hover:border-gray-400"}`}
              aria-label="favori"
            >
              <Heart className={`h-4 w-4 ${liked ? "fill-[#D14318] text-[#D14318]" : "text-gray-700"}`} />
            </button>
            <button className="p-2.5 rounded-full border border-gray-300 hover:border-gray-400 transition-colors" aria-label="partager">
              <Share2 className="h-4 w-4 text-gray-700" />
            </button>
            <button
              onClick={() => scrollTo("a-propos")}
              className="hidden md:inline-flex items-center gap-2 bg-[#1B7B71] text-white font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-[#155f57] transition-colors"
            >
              Réserver une table
            </button>
          </div>
        </div>
      </div>

      {/* Sticky tabs */}
      <div className="sticky top-16 bg-white z-40 border-b border-gray-200 mt-6">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex gap-6">
            {[
              { id: "a-propos", label: "À propos" },
              { id: "menu", label: "Menu" },
              { id: "avis", label: "Avis" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => scrollTo(t.id)}
                className={`relative py-3 text-sm font-semibold transition-colors ${
                  activeTab === t.id ? "text-[#1B7B71]" : "text-gray-700 hover:text-gray-900"
                }`}
              >
                {t.label}
                {activeTab === t.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1B7B71] rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-10 mt-4">
        <div className="lg:col-span-2">
          <About />
          <MenuPreview />
          <FullMenu />
          <Reviews
            customReviews={customReviews}
            onNewReview={onNewReview}
            onOpenPhoto={onOpenPhoto}
          />
          <LocationSection />
          <AwardsSection />
          <HoursSection />
        </div>
        <aside className="lg:col-span-1">
          <ReservationCard />
        </aside>
      </main>

      <Footer />
      <MobileCTA />
    </div>
  );
};

export default RestaurantPage;
